import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../shared/api/axiosInstance';
import {
    CreateNotePayload,
    CreateNoteResponse,
    FetchNotesPayload,
    Note,
    NotesResponse,
    UpdateNotePayload,
    UpdateNoteResponse,
} from '../../shared/types/notesTypes';

export const fetchProjectNotes = createAsyncThunk<Note[], FetchNotesPayload>(
    'notes/fetchProjectNotes',
    async ({ projectId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`projects/${projectId}/notes`);
            console.log('response', response.data.data);

            const notes = response.data.data.map((item: any) => ({
                id: item.id,
                title: item.attributes.title,
                content: item.attributes.content,
                createdAt: item.attributes.created_at,
                updatedAt: item.attributes.updated_at,
            }));

            console.log('no', notes);

            return notes;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            return rejectWithValue(`Failed to fetch notes: ${message}`);
        }
    },
);
export const updateProjectNote = createAsyncThunk<UpdateNoteResponse, UpdateNotePayload>(
    'notes/updateProjectNote',
    async ({ noteId, title, content, isPinned, projectId }, { rejectWithValue }) => {
        const body = {
            data: {
                type: 'project_notes',
                id: noteId,
                attributes: {
                    title,
                    content,
                    isPinned,
                },
            },
        };

        try {
            const response = await axiosInstance.patch(
                `projects/${projectId}/notes/${noteId}`,
                body,
            );

            const note = response.data.data;

            return {
                id: note.id,
                title: note.attributes.title,
                content: note.attributes.content,
                updatedAt: note.attributes.updated_at,
            };
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            return rejectWithValue(`Failed to update note: ${message}`);
        }
    },
);

export const createProjectNote = createAsyncThunk<CreateNoteResponse, CreateNotePayload>(
    'notes/createProjectNote',
    async ({ projectId, title, content }, { rejectWithValue }) => {
        const body = {
            data: {
                type: 'project_notes',
                attributes: {
                    title,
                    content,
                },
            },
        };

        try {
            const response = await axiosInstance.post(`/projects/${projectId}/notes`, body);

            const note = response.data.data;

            return {
                id: note.id,
                title: note.attributes.title,
                content: note.attributes.content,
                createdAt: note.attributes.created_at,
                updatedAt: note.attributes.updated_at,
            };
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Unknown error';
            return rejectWithValue(`Failed to create note: ${message}`);
        }
    },
);

export interface NotesState {
    notes: Note[];
    loading: boolean;
    error: string | null;
}

export const initialState: NotesState = {
    notes: [],
    loading: false,
    error: null,
};

const notesSlice = createSlice({
    name: 'notes',
    initialState,
    reducers: {
        setCurrentNote(state, action: PayloadAction<Note>) {
            state.notes.push(action.payload);
        },
        setProjectNotes(state, action: PayloadAction<Note[]>) {
            state.notes = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchProjectNotes.fulfilled, (state, action) => {
            console.log('action.payload', action.payload);
            state.notes = action.payload;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(fetchProjectNotes.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchProjectNotes.rejected, (state, action) => {
            state.loading = false;
            console.log('fetchProjectNotes rejected', action.error, action.payload);
        });
        builder.addCase(updateProjectNote.fulfilled, (state, action) => {
            state.notes = state.notes.map((note) =>
                note.id === action.payload.id ? { ...note, ...action.payload } : note,
            );
        });
        builder.addCase(updateProjectNote.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateProjectNote.rejected, (state, action) => {
            state.loading = false;
        });
        builder.addCase(createProjectNote.fulfilled, (state, action) => {
            state.notes.push({
                id: action.payload.id,
                title: action.payload.title,
                content: action.payload.content,
                createdAt: action.payload.createdAt,
                updatedAt: action.payload.updatedAt,
            });
        });
        builder.addCase(createProjectNote.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createProjectNote.rejected, (state, action) => {
            state.loading = false;
        });
    },
});

export const { setCurrentNote, setProjectNotes } = notesSlice.actions;

export default notesSlice.reducer;
